import { useEffect, useReducer, useRef } from "react";
import { Input, InputProps } from "./input"; // Shandcn UI Input
import { CheckCheck, CircleHelp, Loader } from "lucide-react";
import { useFetcher } from "react-router-dom";
import { SireneSearchResult } from "@/types/utils/sirene";

interface TextInputProps extends InputProps {
    onValueChange?: (value: string) => void;
    onlineCheck?: boolean;
    /** The Sirene record found (`null`: none, `undefined`: no lookup), with the SIRET (digits) it answers for. */
    setSireneSearchResult?: (result: SireneSearchResult | null | undefined, siret?: string) => void;
}

export const siretValidator = (value: string) => {
    const digits = value.replace(/\D/g, "");
    return digits.length == 14;
};

// French SIRET formatter
export const siretFormatter = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, "");

    // SIRET is 14 digits, grouped as XXX XXX XXX XXXXX
    const groups = [];
    for (let i = 0; i < digits.length && i < 14; i += 3) {
        if (i === 9) {
            // Last group is 5 digits
            groups.push(digits.slice(i, Math.min(i + 5, digits.length)));
            break;
        }
        groups.push(digits.slice(i, Math.min(i + 3, digits.length)));
    }

    return groups.join(" ");
};

export default function SiretInput(props: TextInputProps) {
    const fetcher = useFetcher<SireneSearchResult[]>();
    /**
     * The SIRET (digits) of the last lookup. `fetcher.data` answers for it: a new `load` cancels the one in
     * flight, so an earlier lookup never lands after it. A record found also carries its own `siret`.
     */
    const requested = useRef<string | undefined>(undefined);

    const [value, setValue] = useReducer((_: any, next: string) => {
        const digits = next.replace(/\D/g, "");
        return siretFormatter(digits);
    }, props.value?.toString() || "");

    useEffect(() => {
        setValue(props.value?.toString() || "");
    }, [props.value]);

    useEffect(() => {
        if (props.onlineCheck) {
            if (siretValidator(value)) {
                requested.current = value.replace(/\D/g, "");
                fetcher.load(`/utils/siret/${value}`);
            } else {
                props.setSireneSearchResult?.(undefined); // Reset the result if the SIRET is not valid
            }
        }
    }, [value]);

    function handleChange(v: string) {
        if (props.onValueChange) {
            props.onValueChange(v.replace(/\D/g, "").substring(0, 14));
        }
    }

    useEffect(() => {
        if (fetcher.data !== undefined) {
            if (fetcher.data.length > 0) {
                props.setSireneSearchResult?.(fetcher.data[0], fetcher.data[0].siret ?? requested.current);
            } else {
                props.setSireneSearchResult?.(null, requested.current);
            }
        } else {
            props.setSireneSearchResult?.(undefined);
        }
    }, [fetcher.data]);

    let icon = null;

    if (props.onlineCheck) {
        if (siretValidator(value)) {
            if (fetcher.state !== "idle") {
                icon = Loader;
            } else if (fetcher.data && fetcher.data.length > 0) {
                icon = CheckCheck;
            } else {
                icon = CircleHelp;
            }
        }
    } else {
        icon = siretValidator(value) ? CheckCheck : null;
    }

    return (
        <Input
            {...props}
            type="text"
            onChange={ev => {
                setValue(ev.target.value);
                handleChange(ev.target.value);
            }}
            value={value}
            icon={icon}
        />
    );
}
